import { differenceInMinutes, parseISO } from 'date-fns'
import { REASON_CODES } from '@/features/attendance/schemas/timeCorrection.schema'
import type { BadgeVariant } from '@/components/ui'
import type {
  TimeCorrectionAttendanceRef,
  TimeCorrectionPerson,
  TimeCorrectionRequest,
  TimeCorrectionRequestStatus,
} from '@/features/attendance/types/timeCorrection.types'

export const TIME_CORRECTION_STATUS_VARIANT: Record<
  TimeCorrectionRequestStatus,
  BadgeVariant
> = {
  pending: 'warning',
  approved: 'success',
  rejected: 'danger',
}

export const TIME_CORRECTION_STATUS_LABEL: Record<
  TimeCorrectionRequestStatus,
  string
> = {
  pending: 'Awaiting review',
  approved: 'Approved',
  rejected: 'Rejected',
}

function asPerson(
  value: string | TimeCorrectionPerson | undefined
): TimeCorrectionPerson | null {
  return value && typeof value === 'object' ? value : null
}

export function getRequester(
  request: TimeCorrectionRequest
): TimeCorrectionPerson | null {
  return asPerson(request.requestedBy)
}

export function getReviewer(
  request: TimeCorrectionRequest
): TimeCorrectionPerson | null {
  return asPerson(request.reviewedBy)
}

export function getOriginalAttendance(
  request: TimeCorrectionRequest
): TimeCorrectionAttendanceRef | null {
  return request.attendanceId && typeof request.attendanceId === 'object'
    ? request.attendanceId
    : null
}

/** "Jane Doe", falling back to email, then a neutral label. */
export function personDisplayName(
  person: TimeCorrectionPerson | null | undefined,
  fallback = 'Unknown employee'
): string {
  if (!person) return fallback
  const full = [person.firstName, person.lastName]
    .filter((p) => !!p && p.trim())
    .join(' ')
  return full || person.email || fallback
}

/** "Driver · Operations" when either is set. */
export function personRoleLine(
  person: TimeCorrectionPerson | null | undefined
): string | undefined {
  const parts = [person?.employment?.designation, person?.employment?.department]
    .filter((p): p is string => !!p && p.trim().length > 0)
  return parts.length ? parts.join(' · ') : undefined
}

export function reasonCodeLabel(code: string | undefined): string | undefined {
  if (!code) return undefined
  return (
    REASON_CODES.find((r) => r.value === code)?.label ??
    code.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase())
  )
}

/** Minutes between two ISO timestamps, or null if either is missing / reversed. */
export function minutesBetween(
  start: string | undefined,
  end: string | undefined
): number | null {
  if (!start || !end) return null
  const mins = differenceInMinutes(parseISO(end), parseISO(start))
  return mins >= 0 ? mins : null
}

export function formatMinutes(minutes: number | null | undefined): string {
  if (minutes == null) return '—'
  const h = Math.floor(minutes / 60)
  const m = Math.round(minutes % 60)
  if (h === 0) return `${m}m`
  return m === 0 ? `${h}h` : `${h}h ${m}m`
}
